/* Verqo landing-page hero video — every.io pattern
   ────────────────────────────────────────────────────────────────
   Behavior (copy-paste from every.io):
   1. Loads muted-autoplay-loop-playsInline on mount so mobile allows it.
   2. Hover the video → subtle overlay fades in with a play/unmute button.
   3. Click the video anywhere → unmutes + restarts from 0 + shows chrome
      video controls, hides the overlay.
   4. Click outside / video ends → returns to muted-loop ambient state.

   Kept as a stand-alone component so it can be dropped into Landing.jsx,
   LandingV2Preview.jsx, or a marketing card without extra plumbing.
*/
import { useEffect, useRef, useState } from "react";
import { Play, Volume2, Pause } from "lucide-react";

export default function HeroVideo({
  src        = "/media/verqo-hero.mp4",
  poster     = "/media/verqo-hero-poster.jpg",
  title      = "Watch the Verqo story · 3 min",
  className  = "",
  testid     = "hero-video",
}) {
  const ref = useRef(null);
  const [hovered, setHovered]   = useState(false);
  const [playing, setPlaying]   = useState(true);   // muted autoplay on mount
  const [unmuted, setUnmuted]   = useState(false);

  // Kick off muted autoplay-loop on mount so the hero always has motion.
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted   = true;
    v.loop    = true;
    v.playsInline = true;
    v.play().catch(() => {/* autoplay might be blocked; that's fine */});
  }, []);

  const unmuteAndRestart = () => {
    const v = ref.current;
    if (!v) return;
    v.muted        = false;
    v.loop         = false;
    v.controls     = true;
    v.currentTime  = 0;
    v.play();
    setUnmuted(true);
    setPlaying(true);
  };

  const togglePause = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else          { v.pause(); setPlaying(false); }
  };

  const onEnded = () => {
    // When the sound version ends, return to muted ambient loop.
    const v = ref.current;
    if (!v) return;
    v.muted    = true;
    v.loop     = true;
    v.controls = false;
    v.currentTime = 0;
    v.play();
    setUnmuted(false);
    setPlaying(true);
  };

  return (
    <div
      className={`relative rounded-2xl overflow-hidden bg-black shadow-2xl ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={unmuted ? undefined : unmuteAndRestart}
      data-testid={testid}
      role="button"
      aria-label={title}
    >
      <video
        ref={ref}
        poster={poster}
        src={src}
        preload="metadata"
        className="w-full h-full object-cover"
        onEnded={onEnded}
        data-testid={`${testid}-el`}
      />

      {/* Hover overlay — hidden once user has unmuted + is watching */}
      {!unmuted && (
        <div
          className={`absolute inset-0 flex items-end justify-between p-5 md:p-6
                       bg-gradient-to-t from-black/60 via-black/10 to-transparent
                       transition-opacity duration-300
                       ${hovered ? "opacity-100" : "opacity-70"}`}
          data-testid={`${testid}-overlay`}
        >
          <div className="flex items-center gap-2.5 text-white/90">
            <div className="w-10 h-10 rounded-full bg-lime-400 grid place-items-center shadow-lg">
              <Play className="w-4 h-4 text-[#0a0e0a] fill-current translate-x-[1px]" />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-lime-300/90 mono">
                Click to unmute
              </div>
              <div className="text-sm font-medium tracking-tight">{title}</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.22em] text-white/70 mono">
            <Volume2 className="w-3.5 h-3.5" /> Muted
          </div>
        </div>
      )}

      {/* Once unmuted, offer a light pause pill in the corner */}
      {unmuted && (
        <button
          onClick={togglePause}
          className="absolute top-4 right-4 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 text-white text-[11px] mono uppercase tracking-[0.16em] flex items-center gap-1.5 hover:bg-black/80 transition-colors"
          data-testid={`${testid}-pause`}
        >
          {playing ? <><Pause className="w-3 h-3"/> Pause</> : <><Play className="w-3 h-3"/> Play</>}
        </button>
      )}
    </div>
  );
}
